/**
 * Admin Dashboard — User management with left-list + right-detail layout
 * Tabs: Users | eSIM | APIs | LLM Ops
 * 
 * Phase A: Enhanced with LLM Config management and real vendor health
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { authFetch } from '../../auth';
import { AdminDashboard as EsimAdminDashboard } from '../eSIM/EsimDashboard';
import './AdminDashboard.css';

// API service configurations with direct console links
const API_SERVICES = [
  {
    id: 'openrouter',
    name: 'OpenRouter',
    description: 'LLM routing (Claude, GPT, Gemini fallback)',
    consoleUrl: 'https://openrouter.ai/settings/keys',
    consoleLabel: 'Manage API Keys',
    billingUrl: 'https://openrouter.ai/settings/credits',
    billingLabel: 'Add Credits',
    docsUrl: 'https://openrouter.ai/docs',
    errorCodes: { 402: 'Token limit exceeded', 401: 'Invalid API key', 429: 'Rate limited' }
  },
  {
    id: 'elevenlabs',
    name: 'ElevenLabs',
    description: 'Voice synthesis (TTS)',
    consoleUrl: 'https://elevenlabs.io/app/subscription',
    consoleLabel: 'Subscription',
    billingUrl: 'https://elevenlabs.io/app/subscription',
    billingLabel: 'Add Credits',
    docsUrl: 'https://elevenlabs.io/docs/api-reference',
    errorCodes: { 401: 'Quota exceeded or invalid key', 429: 'Rate limited' }
  },
  {
    id: 'google',
    name: 'Google AI (Gemini)',
    description: 'Primary LLM',
    consoleUrl: 'https://aistudio.google.com/apikey',
    consoleLabel: 'API Keys',
    billingUrl: 'https://console.cloud.google.com/billing',
    billingLabel: 'Billing',
    docsUrl: 'https://ai.google.dev/docs',
    errorCodes: { 403: 'Project denied access', 429: 'Quota exceeded' }
  },
  {
    id: 'deepseek',
    name: 'DeepSeek',
    description: 'Direct LLM (DeepSeek V4)',
    consoleUrl: 'https://platform.deepseek.com/api_keys',
    consoleLabel: 'API Keys',
    billingUrl: 'https://platform.deepseek.com/usage',
    billingLabel: 'Usage',
    docsUrl: 'https://api-docs.deepseek.com/',
    errorCodes: { 401: 'Invalid API key', 402: 'Insufficient balance', 429: 'Rate limited' }
  },
  {
    id: 'perplexity',
    name: 'Perplexity',
    description: 'Sonar models with web grounding (Pro+ only)',
    consoleUrl: 'https://www.perplexity.ai/settings/api',
    consoleLabel: 'API Keys',
    billingUrl: 'https://www.perplexity.ai/settings/api',
    billingLabel: 'Usage',
    docsUrl: 'https://docs.perplexity.ai/',
    errorCodes: { 401: 'Invalid API key', 402: 'Credits exhausted', 429: 'Rate limited' }
  },
  {
    id: 'twilio',
    name: 'Twilio',
    description: 'Voice calls & SMS',
    consoleUrl: 'https://console.twilio.com',
    consoleLabel: 'Console',
    billingUrl: 'https://console.twilio.com/us1/billing/manage-billing/billing-overview',
    billingLabel: 'Billing',
    docsUrl: 'https://www.twilio.com/docs',
    errorCodes: { 401: 'Invalid credentials', 402: 'Payment required' }
  }
];

// Vendor display configs for health data
const VENDOR_DISPLAY = {
  openrouter: { color: '#6366f1', icon: '🤖' },
  elevenlabs: { color: '#10b981', icon: '🔊' },
  google: { color: '#f59e0b', icon: '✨' },
  openai: { color: '#0ea5e9', icon: '🧠' },
  deepseek: { color: '#7c3aed', icon: '🐋' },
  perplexity: { color: '#22c1c3', icon: '🔎' },
  cloudflare: { color: '#f97316', icon: '☁️' },
  minimax: { color: '#ec4899', icon: '🎙️' },
  twilio: { color: '#ef4444', icon: '📞' }
};

const MODEL_ROUTE_PROVIDERS = [
  { id: 'google', label: 'Google Direct' },
  { id: 'openai', label: 'OpenAI Direct' },
  { id: 'deepseek', label: 'DeepSeek Direct' },
  { id: 'perplexity', label: 'Perplexity (Pro+)' },
  { id: 'cloudflare', label: 'Cloudflare Workers AI' },
  { id: 'openrouter', label: 'OpenRouter' }
];

const POLICY_CATEGORIES = [
  { id: 'AGENT', label: 'Agent' },
  { id: 'TTS', label: 'TTS' },
  { id: 'STT', label: 'STT' }
];

const CATEGORY_ROUTE_PROVIDERS = {
  AGENT: MODEL_ROUTE_PROVIDERS,
  TTS: [
    { id: 'elevenlabs', label: 'ElevenLabs' },
    { id: 'edge_tts', label: 'Edge TTS' }
  ],
  STT: [
    { id: 'elevenlabs_stt', label: 'ElevenLabs STT' },
    { id: 'google_stt', label: 'Gemini Live STT' }
  ]
};

const COST_LEVEL_CLASS = {
  very_low: 'is-very-low',
  low: 'is-low',
  medium: 'is-medium',
  high: 'is-high'
};

const COST_BAND_LABEL = {
  very_low: 'Very low',
  low: 'Low',
  medium: 'Medium',
  high: 'High'
};

const ADMIN_TABS = [
  { id: 'users', label: 'Users' },
  { id: 'esim', label: 'eSIM' },
  { id: 'apis', label: 'APIs' },
  { id: 'llmOps', label: 'LLM Ops' }
];

const AdminDashboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [userCalls, setUserCalls] = useState([]);
  const [userSMS, setUserSMS] = useState([]);
  const [userEsims, setUserEsims] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [lastTempPassword, setLastTempPassword] = useState(null);
  const [apiStatus, setApiStatus] = useState({});
  const [apiStatusLoading, setApiStatusLoading] = useState(false);
  
  // Phase A: LLM Configs state
  const [llmConfigs, setLlmConfigs] = useState([]);
  const [llmHealth, setLlmHealth] = useState({});
  const [llmConfigsLoading, setLlmConfigsLoading] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [showAddKey, setShowAddKey] = useState(false);
  const [newKeyValue, setNewKeyValue] = useState('');
  const [configActionMessage, setConfigActionMessage] = useState(null);
  const [llmOpsSummary, setLlmOpsSummary] = useState(null);
  const [llmOpsLoading, setLlmOpsLoading] = useState(false);
  const [planConfigs, setPlanConfigs] = useState([]);
  const [planConfigDrafts, setPlanConfigDrafts] = useState({});
  const [planConfigsLoading, setPlanConfigsLoading] = useState(false);
  const [planSaveState, setPlanSaveState] = useState({});
  const [modelCatalog, setModelCatalog] = useState([]);
  const [selectedPolicyCategory, setSelectedPolicyCategory] = useState('AGENT');

  const modelCatalogById = useMemo(() => (
    (modelCatalog || []).reduce((acc, entry) => {
      acc[entry.id] = entry;
      return acc;
    }, {})
  ), [modelCatalog]);

  const getModelMeta = useCallback((model) => {
    const raw = String(model || '').trim();
    if (!raw) return null;
    return modelCatalog.find((entry) => {
      if (entry.id === raw) return true;
      if ((entry.aliases || []).includes(raw)) return true;
      return Object.values(entry.provider_variants || {}).includes(raw);
    }) || modelCatalogById[raw] || null;
  }, [modelCatalog, modelCatalogById]);

  const formatModelList = useCallback((models) => {
    if (models == null) return '*';
    if (!Array.isArray(models)) return '';
    return models.join('\n');
  }, []);

  const parseModelList = useCallback((raw) => {
    const value = String(raw || '').trim();
    if (!value) return [];
    if (value === '*') return null;
    return value.split('\n').map(item => item.trim()).filter(Boolean);
  }, []);

  const providerListIncludesModel = useCallback((list, provider, model) => {
    if (list == null) return true;
    if (!Array.isArray(list)) return false;
    const normalized = provider === 'google' && String(model || '').startsWith('google/')
      ? String(model).slice('google/'.length)
      : String(model || '');
    return list.includes(model) || list.includes(normalized);
  }, []);

  const getPlanTextModels = useCallback((models) => {
    const lists = ['openrouter', 'google', 'openai'].map((provider) => models?.[provider]);
    if (lists.some((list) => list == null)) return [];
    const union = new Set();
    ['openrouter', 'google', 'openai'].forEach((provider) => {
      const list = models?.[provider];
      if (!Array.isArray(list)) return;
      list.forEach((model) => {
        if (!model) return;
        union.add(provider === 'google' && !String(model).startsWith('google/') ? `google/${model}` : model);
      });
    });
    return Array.from(union).sort();
  }, []);

  const getPlanTtsModels = useCallback((models) => {
    const rows = [];
    const elevenlabs = models?.elevenlabs;
    if (elevenlabs === null) {
      rows.push('elevenlabs/*');
    } else if (Array.isArray(elevenlabs)) {
      elevenlabs.forEach((model) => {
        if (!model) return;
        rows.push(String(model).startsWith('elevenlabs/') ? model : `elevenlabs/${model}`);
      });
    }
    rows.push('edge_tts/default');
    return Array.from(new Set(rows));
  }, []);

  const getPlanCategoryRows = useCallback((draft, category) => {
    if (category === 'AGENT') {
      return getPlanTextModels({
        openrouter: parseModelList(draft.openrouter),
        google: parseModelList(draft.google),
        openai: parseModelList(draft.openai)
      });
    }
    if (category === 'TTS') {
      return getPlanTtsModels({
        elevenlabs: parseModelList(draft.elevenlabs)
      });
    }
    return [];
  }, [getPlanTextModels, getPlanTtsModels, parseModelList]);

  const getEnabledProvidersForRow = useCallback((draft, category, model) => {
    if (category === 'AGENT') {
      return CATEGORY_ROUTE_PROVIDERS.AGENT.filter(provider => providerListIncludesModel(parseModelList(draft[provider.id]), provider.id, model));
    }
    if (category === 'TTS') {
      if (model === 'edge_tts/default') return CATEGORY_ROUTE_PROVIDERS.TTS.filter(provider => provider.id === 'edge_tts');
      return CATEGORY_ROUTE_PROVIDERS.TTS.filter(provider => provider.id === 'elevenlabs');
    }
    return [];
  }, [parseModelList, providerListIncludesModel]);

  const buildDefaultProviderPriorities = useCallback((models) => {
    const rows = {};
    getPlanTextModels(models).forEach((model) => {
      const enabled = MODEL_ROUTE_PROVIDERS
        .map((provider) => provider.id)
        .filter((provider) => providerListIncludesModel(models?.[provider], provider, model));
      enabled.sort((a, b) => {
        const aDirect = model.startsWith(`${a}/`) ? 0 : 1;
        const bDirect = model.startsWith(`${b}/`) ? 0 : 1;
        if (aDirect !== bDirect) return aDirect - bDirect;
        if (a === 'openrouter') return 1;
        if (b === 'openrouter') return -1;
        return a.localeCompare(b);
      });
      rows[model] = enabled.reduce((acc, provider, index) => {
        acc[provider] = index + 1;
        return acc;
      }, {});
    });
    return rows;
  }, [getPlanTextModels, providerListIncludesModel]);

  const buildPlanDraft = useCallback((plan) => ({
    openrouter: formatModelList(plan?.models?.openrouter),
    google: formatModelList(plan?.models?.google),
    openai: formatModelList(plan?.models?.openai),
    elevenlabs: formatModelList(plan?.models?.elevenlabs),
    model_provider_priorities: plan?.model_provider_priorities || buildDefaultProviderPriorities(plan?.models || {})
  }), [buildDefaultProviderPriorities, formatModelList]);

  const loadPlanConfigs = useCallback(async () => {
    setPlanConfigsLoading(true);
    try {
      const resp = await authFetch('/api/admin/plans?include_hidden=true');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const plans = Array.isArray(data.plans) ? data.plans : [];
      setPlanConfigs(plans);
      setPlanConfigDrafts(plans.reduce((acc, plan) => {
        acc[plan.plan_id] = buildPlanDraft(plan);
        return acc;
      }, {}));
    } catch (err) {
      setConfigActionMessage({ type: 'error', text: `Failed to load plan model policies: ${err.message}` });
    } finally {
      setPlanConfigsLoading(false);
    }
  }, [buildPlanDraft]);

  const loadModelCatalog = useCallback(async () => {
    try {
      const resp = await authFetch('/api/aiassistant/models');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setModelCatalog(Array.isArray(data.models) ? data.models : []);
    } catch (err) {
      console.error('Failed to load model catalog:', err);
    }
  }, []);

  const updatePlanDraft = useCallback((planId, vendor, value) => {
    setPlanConfigDrafts(prev => ({
      ...prev,
      [planId]: {
        ...(prev[planId] || {}),
        [vendor]: value
      }
    }));
  }, []);

  const handleSavePlanModels = useCallback(async (plan) => {
    const draft = planConfigDrafts[plan.plan_id] || buildPlanDraft(plan);
    const models = {
      ...(plan.models || {}),
      openrouter: parseModelList(draft.openrouter),
      google: parseModelList(draft.google),
      openai: parseModelList(draft.openai),
      elevenlabs: parseModelList(draft.elevenlabs)
    };

    setPlanSaveState(prev => ({ ...prev, [plan.plan_id]: 'saving' }));
    try {
      const resp = await authFetch(`/api/admin/plans/${plan.plan_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ models, model_provider_priorities: draft.model_provider_priorities || {} })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);

      const updatedPlan = data.plan || { ...plan, models, model_provider_priorities: draft.model_provider_priorities || {} };
      setPlanConfigs(prev => prev.map(item => item.plan_id === plan.plan_id ? updatedPlan : item));
      setPlanConfigDrafts(prev => ({
        ...prev,
        [plan.plan_id]: buildPlanDraft(updatedPlan)
      }));
      setPlanSaveState(prev => ({ ...prev, [plan.plan_id]: 'saved' }));
      setConfigActionMessage({ type: 'success', text: `Saved model policy for ${plan.plan_id}` });
      window.setTimeout(() => {
        setPlanSaveState(prev => prev[plan.plan_id] ? { ...prev, [plan.plan_id]: null } : prev);
      }, 2000);
    } catch (err) {
      setPlanSaveState(prev => ({ ...prev, [plan.plan_id]: 'error' }));
      setConfigActionMessage({ type: 'error', text: `Failed to save ${plan.plan_id}: ${err.message}` });
    }
  }, [buildPlanDraft, parseModelList, planConfigDrafts]);

  const updateProviderPriority = useCallback((planId, model, provider, nextValue) => {
    setPlanConfigDrafts((prev) => {
      const draft = prev[planId] || {};
      const priorities = { ...(draft.model_provider_priorities || {}) };
      const row = { ...(priorities[model] || {}) };
      const target = parseInt(nextValue, 10);
      if (!target) return prev;
      Object.keys(row).forEach((key) => {
        if (key === provider) return;
        if (row[key] >= target) row[key] += 1;
      });
      row[provider] = target;
      const ordered = Object.entries(row)
        .sort((a, b) => a[1] - b[1])
        .reduce((acc, [key], index) => {
          acc[key] = index + 1;
          return acc;
        }, {});
      priorities[model] = ordered;
      return {
        ...prev,
        [planId]: {
          ...draft,
          model_provider_priorities: priorities
        }
      };
    });
  }, []);

  // Load LLM configs
  const loadLlmConfigs = useCallback(async () => {
    setLlmConfigsLoading(true);
    try {
      const [configsResp, healthResp] = await Promise.all([
        authFetch('/api/admin/llm-configs'),
        authFetch('/api/admin/llm-configs/health/all')
      ]);
      
      if (configsResp.ok) {
        const data = await configsResp.json();
        setLlmConfigs(data.configs || []);
      }
      
      if (healthResp.ok) {
        const data = await healthResp.json();
        setLlmHealth(data.health || {});
      }
    } catch (err) {
      console.error('Failed to load LLM configs:', err);
    } finally {
      setLlmConfigsLoading(false);
    }
  }, []);

  // Add a new key to a config
  const handleAddKey = useCallback(async (configId) => {
    if (!newKeyValue.trim()) {
      setConfigActionMessage({ type: 'error', text: 'API key is required' });
      return;
    }
    
    try {
      const resp = await authFetch(`/api/admin/llm-configs/${configId}/keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: newKeyValue, set_primary: false })
      });
      
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || 'Failed to add key');
      }
      
      setConfigActionMessage({ type: 'success', text: 'Key added successfully' });
      setNewKeyValue('');
      setShowAddKey(false);
      loadLlmConfigs();
    } catch (err) {
      setConfigActionMessage({ type: 'error', text: err.message });
    }
  }, [newKeyValue, loadLlmConfigs]);

  // Set a key as primary
  const handleSetPrimary = useCallback(async (configId, keyId) => {
    try {
      const resp = await authFetch(`/api/admin/llm-configs/${configId}/keys/${keyId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_primary: true })
      });
      
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || 'Failed to set primary');
      }
      
      setConfigActionMessage({ type: 'success', text: 'Primary key updated' });
      loadLlmConfigs();
    } catch (err) {
      setConfigActionMessage({ type: 'error', text: err.message });
    }
  }, [loadLlmConfigs]);

  // Revoke a key
  const handleRevokeKey = useCallback(async (configId, keyId) => {
    if (!window.confirm('Are you sure you want to revoke this key?')) return;
    
    try {
      const resp = await authFetch(`/api/admin/llm-configs/${configId}/keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'manual revocation' })
      });
      
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || 'Failed to revoke key');
      }
      
      setConfigActionMessage({ type: 'success', text: 'Key revoked' });
      loadLlmConfigs();
    } catch (err) {
      setConfigActionMessage({ type: 'error', text: err.message });
    }
  }, [loadLlmConfigs]);

  // Load API status
  const loadApiStatus = useCallback(async () => {
    setApiStatusLoading(true);
    try {
      const resp = await authFetch('/api/admin/api-status');
      if (resp.ok) {
        const data = await resp.json();
        setApiStatus(data.status || {});
      }
    } catch (err) {
      console.error('Failed to load API status:', err);
    } finally {
      setApiStatusLoading(false);
    }
  }, []);

  const loadLlmOpsSummary = useCallback(async () => {
    setLlmOpsLoading(true);
    try {
      const params = new URLSearchParams({
        config_id: 'openrouter_prod',
        user_email: user?.email || 'nfodor@mac.com',
        model: 'google/gemini-3-flash-preview',
        voice_max_tokens: '200'
      });
      const resp = await authFetch(`/api/admin/llm-ops-summary?${params.toString()}`);
      if (resp.ok) {
        const data = await resp.json();
        setLlmOpsSummary(data.summary || null);
      }
    } catch (err) {
      console.error('Failed to load LLM ops summary:', err);
    } finally {
      setLlmOpsLoading(false);
    }
  }, [user?.email]);

  // Load tab-specific admin data on demand.
  useEffect(() => {
    if (activeTab === 'apis') {
      loadApiStatus();
      loadLlmConfigs();
      loadPlanConfigs();
      loadModelCatalog();
    }
    if (activeTab === 'llmOps') {
      loadLlmOpsSummary();
    }
  }, [activeTab, loadApiStatus, loadLlmConfigs, loadLlmOpsSummary, loadPlanConfigs, loadModelCatalog]);

  // Load users on mount and search change
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = useCallback(async (searchTerm) => {
    setLoading(true);
    setError(null);
    try {
      const q = searchTerm !== undefined ? searchTerm : search;
      const params = new URLSearchParams({ limit: '200' });
      if (q) params.set('search', q);
      const resp = await authFetch(`/api/admin/users?${params}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setUsers(data.users || []);
      setTotalUsers(data.total || 0);
    } catch (err) {
      setError('Failed to load users: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  const handleSearch = useCallback((e) => {
    const val = e.target.value;
    setSearch(val);
    // Debounce: load after typing stops
    clearTimeout(window._adminSearchTimeout);
    window._adminSearchTimeout = setTimeout(() => loadUsers(val), 300);
  }, [loadUsers]);

  const selectUser = useCallback(async (userItem) => {
    setSelectedUser(userItem.email);
    setDetailLoading(true);
    setUserCalls([]);
    setUserSMS([]);
    setUserEsims([]);
    setActionMessage(null);
    setLastTempPassword(null);

    try {
      const enc = encodeURIComponent(userItem.email);
      const [detailResp, callsResp, smsResp, esimsResp] = await Promise.all([
        authFetch(`/api/admin/users/${enc}`),
        authFetch(`/api/admin/users/${enc}/calls`).catch(() => null),
        authFetch(`/api/admin/users/${enc}/sms`).catch(() => null),
        authFetch(`/api/admin/users/${enc}/esims`).catch(() => null)
      ]);
      const detail = detailResp.ok ? await detailResp.json() : {};
      const calls = callsResp && callsResp.ok ? await callsResp.json() : { calls: [] };
      const sms = smsResp && smsResp.ok ? await smsResp.json() : { messages: [] };
      const esims = esimsResp && esimsResp.ok ? await esimsResp.json() : { esims: [] };
      setUserDetail(detail.user || null);
      setUserCalls(calls.calls || []);
      setUserSMS(sms.messages || []);
      setUserEsims(esims.esims || []);
    } catch (err) {
      setError('Failed to load user detail');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleResetPassword = useCallback(async () => {
    if (!selectedUser) return;
    if (!window.confirm(`Reset password for ${selectedUser}?`)) return;

    try {
      const resp = await authFetch(`/api/admin/users/${encodeURIComponent(selectedUser)}/reset-password`, {
        method: 'POST'
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setLastTempPassword(data.tempPassword);
      setActionMessage(`Password reset. Temp password: ${data.tempPassword}`);
    } catch (err) {
      setActionMessage('Failed to reset password: ' + err.message);
    }
  }, [selectedUser]);

  const handleSendLoginInfo = useCallback(async () => {
    if (!selectedUser || !lastTempPassword) return;
    if (!window.confirm(`Send login info email to ${selectedUser}?`)) return;

    try {
      const resp = await authFetch(`/api/admin/users/${encodeURIComponent(selectedUser)}/send-login-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempPassword: lastTempPassword })
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setActionMessage(data.message);
      setLastTempPassword(null);
    } catch (err) {
      setActionMessage('Failed to send login info: ' + err.message);
    }
  }, [selectedUser, lastTempPassword]);

  const handleToggleStatus = useCallback(async () => {
    if (!selectedUser || !userDetail) return;
    const action = userDetail.disabled ? 'enable' : 'disable';
    if (!window.confirm(`${action} account for ${selectedUser}?`)) return;

    try {
      const resp = await authFetch(`/api/admin/users/${encodeURIComponent(selectedUser)}/toggle-status`, {
        method: 'POST'
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setActionMessage(data.message);
      setUserDetail(prev => ({ ...prev, disabled: data.disabled }));
      // Update in list too
      setUsers(prev => prev.map(u =>
        u.email === selectedUser ? { ...u, disabled: data.disabled } : u
      ));
    } catch (err) {
      setActionMessage('Failed to toggle status: ' + err.message);
    }
  }, [selectedUser, userDetail]);

  // Filter users client-side for instant results on loaded data
  const filteredUsers = useMemo(() => {
    return users;
  }, [users]);

  const formatDate = (d) => {
    if (!d) return 'Never';
    const date = new Date(d);
    if (isNaN(date.getTime())) return 'Never';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const renderFailureContext = useCallback((failure) => {
    if (!failure) return null;
    const fields = [
      ['Requested', failure.requested_model],
      ['Effective', failure.effective_model],
      ['Route', failure.route],
      ['Stage', failure.fallback_stage],
      ['Source', failure.source_label || failure.source],
      ['Role', failure.role_label || failure.caller_tier],
      ['User', failure.user_email],
      ['Plan', failure.plan_tier],
      ['Kind', failure.failure_kind || failure.transport_error_code]
    ].filter(([, value]) => value);
    if (!fields.length) return null;
    return (
      <div className="llm-failure-context">
        {fields.map(([label, value]) => (
          <div key={`${label}-${value}`} className="llm-failure-chip">
            <span>{label}</span>
            <code>{value}</code>
          </div>
        ))}
      </div>
    );
  }, []);

  const isActive = (u) => {
    if (u.disabled) return false;
    if (!u.lastconnect) return false;
    const last = new Date(u.lastconnect);
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    return last > thirtyDaysAgo;
  };

  const getUsageClass = (percent) => {
    if (percent >= 95) return 'usage-critical';
    if (percent >= 80) return 'usage-warning';
    return 'usage-ok';
  };

  if (activeTab === 'apis') {
    return (
      <div className="admin-dashboard">
        <div className="admin-header">
          <div className="admin-header-content">
            <h1>Admin Dashboard</h1>
            <span className="admin-user-count">{totalUsers} users</span>
          </div>
          <div className="admin-tabs">
            {ADMIN_TABS.map(tab => (
              <button key={tab.id} className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>{tab.label}</button>
            ))}
          </div>
        </div>
        <div className="admin-apis-content">
          {/* Action messages */}
          {configActionMessage && (
            <div className={`admin-action-message ${configActionMessage.type}`}>
              {configActionMessage.text}
              <button onClick={() => setConfigActionMessage(null)}>×</button>
            </div>
          )}

          <div className="admin-apis-section">
            <div className="admin-apis-header">
              <div>
                <h2>Plan Model Policies</h2>
                <p className="admin-section-subtitle">Edit allowed models per plan. Use <code>*</code> for all models.</p>
              </div>
              <div className="admin-policy-toolbar">
                <select
                  className="admin-policy-category-select"
                  value={selectedPolicyCategory}
                  onChange={e => setSelectedPolicyCategory(e.target.value)}
                >
                  {POLICY_CATEGORIES.map(category => (
                    <option key={category.id} value={category.id}>{category.label}</option>
                  ))}
                </select>
                <button className="admin-action-btn" onClick={loadPlanConfigs} disabled={planConfigsLoading}>
                  {planConfigsLoading ? 'Loading...' : 'Refresh Plans'}
                </button>
              </div>
            </div>

            {planConfigs.length === 0 && !planConfigsLoading ? (
              <div className="admin-empty-state">
                <p>No plans found.</p>
              </div>
            ) : (
              <div className="admin-plan-grid">
                {planConfigs.map(plan => {
                  const draft = planConfigDrafts[plan.plan_id] || buildPlanDraft(plan);
                  const saveState = planSaveState[plan.plan_id] || null;
                  return (
                    <div key={plan.plan_id} className="admin-plan-card">
                      <div className="admin-plan-card-header">
                        <div>
                          <h3>{plan.name}</h3>
                          <div className="admin-plan-meta">
                            <span className="admin-plan-badge">{plan.plan_id}</span>
                            <span>${plan?.ai_limits?.daily_spend == null ? 'custom' : `${plan.ai_limits.daily_spend}/day`}</span>
                          </div>
                        </div>
                        <button
                          className={`admin-btn-sm admin-btn-primary ${saveState === 'saved' ? 'is-saved' : ''}`}
                          onClick={() => handleSavePlanModels(plan)}
                          disabled={saveState === 'saving'}
                        >
                          {saveState === 'saving' ? 'Saving...' : saveState === 'saved' ? 'Saved' : 'Save'}
                        </button>
                      </div>

                      <div className="admin-plan-fields">
                        <label className="admin-plan-field">
                          <span>OpenRouter</span>
                          <textarea
                            className="admin-plan-textarea"
                            value={draft.openrouter}
                            onChange={e => updatePlanDraft(plan.plan_id, 'openrouter', e.target.value)}
                            spellCheck="false"
                          />
                        </label>
                        <label className="admin-plan-field">
                          <span>Google Direct</span>
                          <textarea
                            className="admin-plan-textarea"
                            value={draft.google}
                            onChange={e => updatePlanDraft(plan.plan_id, 'google', e.target.value)}
                            spellCheck="false"
                          />
                        </label>
                        <label className="admin-plan-field">
                          <span>OpenAI Direct</span>
                          <textarea
                            className="admin-plan-textarea"
                            value={draft.openai}
                            onChange={e => updatePlanDraft(plan.plan_id, 'openai', e.target.value)}
                            spellCheck="false"
                          />
                        </label>
                        <label className="admin-plan-field">
                          <span>ElevenLabs</span>
                          <textarea
                            className="admin-plan-textarea"
                            value={draft.elevenlabs}
                            onChange={e => updatePlanDraft(plan.plan_id, 'elevenlabs', e.target.value)}
                            spellCheck="false"
                          />
                        </label>
                      </div>

                      <div className="admin-plan-routing">
                        <div className="admin-plan-routing-header">
                          <strong>{POLICY_CATEGORIES.find(item => item.id === selectedPolicyCategory)?.label || 'Agent'} Priority</strong>
                          <span>
                            {selectedPolicyCategory === 'AGENT'
                              ? 'Unique order per model row'
                              : selectedPolicyCategory === 'TTS'
                                ? 'Provider matrix for voice synthesis'
                                : 'Provider matrix for speech recognition'}
                          </span>
                        </div>
                        <div className="admin-plan-routing-table-wrap">
                          {selectedPolicyCategory === 'STT' ? (
                            <div className="admin-empty-state admin-inline-empty">
                              <p>STT policy routing is not configured yet.</p>
                            </div>
                          ) : (
                          <table className="admin-plan-routing-table">
                            <thead>
                              <tr>
                                <th>Model</th>
                                {CATEGORY_ROUTE_PROVIDERS[selectedPolicyCategory].map(provider => (
                                  <th key={provider.id}>{provider.label}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {getPlanCategoryRows(draft, selectedPolicyCategory).map((model) => {
                                const modelMeta = getModelMeta(model);
                                const row = (draft.model_provider_priorities || {})[model] || {};
                                const enabledProviders = getEnabledProvidersForRow(draft, selectedPolicyCategory, model);
                                return (
                                  <tr key={model}>
                                    <td>
                                      <div className="admin-model-label">{modelMeta?.display_name || model}</div>
                                      <div className="admin-model-meta">
                                        <code>{modelMeta?.id || model}</code>
                                        {modelMeta?.status === 'preview' && <span className="admin-model-chip">preview</span>}
                                        {modelMeta?.tier && <span className="admin-model-chip">{modelMeta.tier}</span>}
                                      </div>
                                    </td>
                                    {CATEGORY_ROUTE_PROVIDERS[selectedPolicyCategory].map((provider) => {
                                      const enabled = enabledProviders.some(item => item.id === provider.id);
                                      const costProfile = modelMeta?.cost_profiles?.[provider.id] || null;
                                      return (
                                        <td key={`${model}-${provider.id}`}>
                                          {enabled ? (
                                            <div className="admin-route-cell">
                                              <select
                                                className="admin-plan-priority-select"
                                                value={row[provider.id] || ''}
                                                onChange={e => updateProviderPriority(plan.plan_id, model, provider.id, e.target.value)}
                                              >
                                                {enabledProviders.map((_, index) => (
                                                  <option key={`${provider.id}-${index + 1}`} value={index + 1}>{index + 1}</option>
                                                ))}
                                              </select>
                                              {costProfile && (
                                                <>
                                                  <span className={`admin-cost-badge ${COST_LEVEL_CLASS[costProfile.cost_band] || ''}`}>
                                                    {COST_BAND_LABEL[costProfile.cost_band] || costProfile.cost_band}
                                                  </span>
                                                  <div className="admin-cost-rank">Rank {costProfile.cost_rank} · {costProfile.cost_source} · {costProfile.cost_confidence}</div>
                                                  {(costProfile.note || costProfile.admin_note) && (
                                                    <div className="admin-cost-note">{costProfile.note || costProfile.admin_note}</div>
                                                  )}
                                                </>
                                              )}
                                            </div>
                                          ) : (
                                            <span className="admin-plan-priority-na">—</span>
                                          )}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* LLM Configs Section */}
          <div className="admin-apis-section">
            <div className="admin-apis-header">
              <h2>LLM Configurations</h2>
              <button className="admin-action-btn" onClick={loadLlmConfigs} disabled={llmConfigsLoading}>
                {llmConfigsLoading ? 'Loading...' : 'Refresh Health'}
              </button>
            </div>
            
            {llmConfigs.length === 0 && !llmConfigsLoading ? (
              <div className="admin-empty-state">
                <p>No LLM configurations found.</p>
                <p className="admin-empty-hint">Configs will be created when you seed from environment variables.</p>
              </div>
            ) : (
              <div className="admin-llm-configs-grid">
                {llmConfigs.map(config => {
                  const health = llmHealth[config.config_id] || {};
                  const vendorStyle = VENDOR_DISPLAY[config.vendor] || { color: '#64748b', icon: '⚙️' };
                  const isExpanded = selectedConfig === config.config_id;
                  
                  // Calculate usage percentage for display
                  let usagePercent = null;
                  let usageText = null;
                  
                  if (config.vendor === 'elevenlabs' && health.subscription) {
                    usagePercent = health.subscription.usage_percent;
                    usageText = `${health.subscription.character_count?.toLocaleString()} / ${health.subscription.character_limit?.toLocaleString()} chars`;
                  } else if (config.vendor === 'openrouter' && health.usage) {
                    if (health.usage.limit) {
                      usagePercent = Math.round((health.usage.used / health.usage.limit) * 100);
                      usageText = `$${health.usage.used?.toFixed(2)} / $${health.usage.limit} daily`;
                    } else {
                      usageText = `$${health.usage.used?.toFixed(2)} used`;
                    }
                  }
                  
                  return (
                    <div key={config.config_id} className={`admin-llm-config-card ${health.healthy === false ? 'unhealthy' : ''}`}>
                      <div className="llm-config-header" onClick={() => setSelectedConfig(isExpanded ? null : config.config_id)}>
                        <div className="llm-config-title">
                          <span className="llm-vendor-icon">{vendorStyle.icon}</span>
                          <div>
                            <h3>{config.name}</h3>
                            <span className="llm-slot-badge" style={{ backgroundColor: vendorStyle.color }}>{config.slot_type}</span>
                          </div>
                        </div>
                        <div className="llm-config-status">
                          {health.healthy === false ? (
                            <span className="health-badge unhealthy">Unhealthy</span>
                          ) : health.healthy === true ? (
                            <span className="health-badge healthy">Healthy</span>
                          ) : (
                            <span className="health-badge unknown">Unknown</span>
                          )}
                          <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                        </div>
                      </div>
                      
                      {/* Usage bar */}
                      {usagePercent !== null && (
                        <div className="llm-usage-container">
                          <div className="llm-usage-bar">
                            <div 
                              className={`llm-usage-fill ${getUsageClass(usagePercent)}`} 
                              style={{ width: `${Math.min(usagePercent, 100)}%` }}
                            />
                          </div>
                          <span className="llm-usage-text">{usageText} ({usagePercent}%)</span>
                        </div>
                      )}
                      
                      {/* Vendor-specific info */}
                      {config.vendor === 'elevenlabs' && health.subscription && (
                        <div className="llm-vendor-info">
                          <span>Tier: {health.subscription.tier}</span>
                          {health.subscription.next_reset && (
                            <span>Resets: {new Date(health.subscription.next_reset).toLocaleDateString()}</span>
                          )}
                        </div>
                      )}
                      
                      {config.vendor === 'google' && health.healthy && (
                        <div className="llm-vendor-info">
                          <span>{health.gemini_models} Gemini models available</span>
                        </div>
                      )}
                      
                      {/* Error display */}
                      {health.error && (
                        <div className="llm-error-display">
                          <strong>Error:</strong> {health.error}
                          {health.status_code && <span className="error-code">HTTP {health.status_code}</span>}
                        </div>
                      )}
                      
                      {/* Unhealthy reason display (for credit/threshold issues) */}
                      {health.unhealthy_reason && !health.error && (
                        <div className="llm-error-display">
                          <strong>Issue:</strong> {health.unhealthy_reason}
                        </div>
                      )}
                      
                      {/* Alert thresholds triggered */}
                      {health.thresholds && health.thresholds.length > 0 && (
                        <div className="llm-alerts">
                          {health.thresholds.map((alert, i) => (
                            <div key={i} className={`llm-alert llm-alert-${alert.type}`}>
                              {alert.type === 'critical' ? '🚨' : '⚠️'} {alert.message} ({alert.percent}%)
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="llm-config-details">
                          <div className="llm-detail-section">
                            <h4>Configuration</h4>
                            <div className="llm-detail-grid">
                              <div><label>Config ID:</label> <code>{config.config_id}</code></div>
                              <div><label>Vendor:</label> {config.vendor}</div>
                              <div><label>Model:</label> {config.model || 'N/A'}</div>
                              <div><label>Status:</label> {config.status}</div>
                            </div>
                          </div>
                          
                          <div className="llm-detail-section">
                            <div className="llm-keys-header">
                              <h4>API Keys ({config.keys?.length || 0})</h4>
                              <button 
                                className="admin-btn-sm" 
                                onClick={() => { setShowAddKey(config.config_id); setNewKeyValue(''); }}
                              >
                                + Add Key
                              </button>
                            </div>
                            
                            {showAddKey === config.config_id && (
                              <div className="llm-add-key-form">
                                <input
                                  type="password"
                                  placeholder="Paste new API key..."
                                  value={newKeyValue}
                                  onChange={e => setNewKeyValue(e.target.value)}
                                  className="admin-input"
                                />
                                <button className="admin-btn-sm admin-btn-primary" onClick={() => handleAddKey(config.config_id)}>
                                  Add
                                </button>
                                <button className="admin-btn-sm" onClick={() => setShowAddKey(false)}>
                                  Cancel
                                </button>
                              </div>
                            )}
                            
                            <div className="llm-keys-list">
                              {(config.keys || []).map(key => (
                                <div key={key.key_id} className={`llm-key-item ${key.status === 'revoked' ? 'revoked' : ''}`}>
                                  <div className="llm-key-info">
                                    <code>{key.key_preview}</code>
                                    {key.is_primary && <span className="primary-badge">Primary</span>}
                                    {key.status === 'revoked' && <span className="revoked-badge">Revoked</span>}
                                  </div>
                                  <div className="llm-key-actions">
                                    {key.status === 'active' && !key.is_primary && (
                                      <button 
                                        className="admin-btn-xs"
                                        onClick={() => handleSetPrimary(config.config_id, key.key_id)}
                                      >
                                        Set Primary
                                      </button>
                                    )}
                                    {key.status === 'active' && (
                                      <button 
                                        className="admin-btn-xs admin-btn-danger"
                                        onClick={() => handleRevokeKey(config.config_id, key.key_id)}
                                      >
                                        Revoke
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Quick links */}
                          <div className="llm-detail-section">
                            <h4>Quick Links</h4>
                            <div className="api-card-links">
                              {API_SERVICES.find(s => s.id === config.vendor) && (
                                <>
                                  <a 
                                    href={API_SERVICES.find(s => s.id === config.vendor).consoleUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="api-link api-link-primary"
                                  >
                                    Manage Keys
                                  </a>
                                  <a 
                                    href={API_SERVICES.find(s => s.id === config.vendor).billingUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="api-link"
                                  >
                                    Billing
                                  </a>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Legacy API Services Section (for services not yet in LLM configs) */}
          <div className="admin-apis-section">
            <div className="admin-apis-header">
              <h2>Service Status</h2>
              <button className="admin-action-btn" onClick={loadApiStatus} disabled={apiStatusLoading}>
                {apiStatusLoading ? 'Checking...' : 'Check Errors'}
              </button>
            </div>
            <div className="admin-apis-grid">
              {API_SERVICES.map(service => {
                const status = apiStatus[service.id] || {};
                const hasError = status.failureCount > 0;
                const statusClass = hasError ? 'api-status-error' : 'api-status-ok';
                return (
                  <div key={service.id} className={`admin-api-card ${statusClass}`}>
                    <div className="api-card-header">
                      <h3>{service.name}</h3>
                      {hasError && <span className="api-error-badge">{status.failureCount} failures</span>}
                    </div>
                    <p className="api-description">{service.description}</p>
                    {hasError && status.lastError && (
                      <div className="api-error-detail">
                        <strong>Last error:</strong> {status.lastError.substring(0, 100)}
                        {status.statusCode && <span className="api-status-code">HTTP {status.statusCode}</span>}
                      </div>
                    )}
                    {hasError && service.errorCodes[status.statusCode] && (
                      <div className="api-error-hint">
                        <strong>Fix:</strong> {service.errorCodes[status.statusCode]}
                      </div>
                    )}
                    <div className="api-card-links">
                      <a href={service.consoleUrl} target="_blank" rel="noopener noreferrer" className="api-link api-link-primary">
                        {service.consoleLabel}
                      </a>
                      <a href={service.billingUrl} target="_blank" rel="noopener noreferrer" className="api-link">
                        {service.billingLabel}
                      </a>
                      <a href={service.docsUrl} target="_blank" rel="noopener noreferrer" className="api-link api-link-secondary">
                        Docs
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (activeTab === 'llmOps') {
    return (
      <div className="admin-dashboard">
        <div className="admin-header">
          <div className="admin-header-content">
            <h1>Admin Dashboard</h1>
            <span className="admin-user-count">{totalUsers} users</span>
          </div>
          <div className="admin-tabs">
            {ADMIN_TABS.map(tab => (
              <button key={tab.id} className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>{tab.label}</button>
            ))}
          </div>
        </div>
        <div className="admin-apis-content">
          <div className="admin-apis-section">
            <div className="admin-apis-header">
              <div>
                <h2>LLM Incidents & Mitigations</h2>
                <p className="admin-section-subtitle">Operational failures, routed key headroom, and immediate mitigation links.</p>
              </div>
              <button className="admin-action-btn" onClick={loadLlmOpsSummary} disabled={llmOpsLoading}>
                {llmOpsLoading ? 'Refreshing...' : 'Refresh Incidents'}
              </button>
            </div>

            {llmOpsSummary ? (
              <div className="admin-llm-ops-grid">
                <div className="admin-llm-ops-card">
                  <div className="api-card-header">
                    <h3>Latest Failure</h3>
                    {llmOpsSummary.latest_failure?.statusCode && (
                      <span className="api-error-badge">HTTP {llmOpsSummary.latest_failure.statusCode}</span>
                    )}
                  </div>
                  {llmOpsSummary.latest_failure ? (
                    <>
                      <p className="api-description">
                        {llmOpsSummary.latest_failure.provider_host || llmOpsSummary.latest_failure.provider || 'Unknown provider'}
                      </p>
                      {renderFailureContext(llmOpsSummary.latest_failure)}
                      <div className="api-error-detail">
                        <strong>Error:</strong> {llmOpsSummary.latest_failure.lastError || 'Unknown failure'}
                      </div>
                      <div className="llm-ops-meta">
                        <span>Count: {llmOpsSummary.latest_failure.count || 0}</span>
                        <span>Last seen: {formatDateTime(llmOpsSummary.latest_failure.lastFailure)}</span>
                      </div>
                    </>
                  ) : (
                    <p className="api-description">No recent LLM failures recorded.</p>
                  )}
                </div>

                <div className="admin-llm-ops-card">
                  <div className="api-card-header">
                    <h3>Recent Incidents</h3>
                  </div>
                  {llmOpsSummary.recent_incidents?.length ? (
                    <div className="llm-incident-list">
                      {llmOpsSummary.recent_incidents.slice(0, 5).map((incident, idx) => (
                        <div key={`${incident.service}-${incident.occurredAt || idx}`} className="llm-incident-item">
                          <div className="llm-incident-topline">
                            <strong>{incident.effective_model || incident.requested_model || incident.provider || incident.service}</strong>
                            {incident.statusCode && <span className="api-status-code">HTTP {incident.statusCode}</span>}
                          </div>
                          {renderFailureContext(incident)}
                          <div className="api-error-detail">
                            <strong>Error:</strong> {incident.lastError || 'Unknown failure'}
                          </div>
                          <div className="llm-ops-meta">
                            <span>Count: {incident.count || 1}</span>
                            <span>{formatDateTime(incident.occurredAt || incident.lastFailure)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="api-description">No persisted LLM incidents yet.</p>
                  )}
                </div>

                <div className="admin-llm-ops-card">
                  <div className="api-card-header">
                    <h3>Routed Key Headroom</h3>
                    {llmOpsSummary.key_usage?.usage_percent !== undefined && (
                      <span className={`health-badge ${getUsageClass(llmOpsSummary.key_usage.usage_percent).replace('usage-', '')}`}>
                        {llmOpsSummary.key_usage.usage_percent}% used
                      </span>
                    )}
                  </div>
                  {llmOpsSummary.key_usage ? (
                    <>
                      <p className="api-description">
                        Tier: <strong>{llmOpsSummary.key_usage.tier || llmOpsSummary.routing?.tier || 'unknown'}</strong>
                      </p>
                      <div className="llm-usage-container compact">
                        <div className="llm-usage-bar">
                          <div
                            className={`llm-usage-fill ${getUsageClass(llmOpsSummary.key_usage.usage_percent || 0)}`}
                            style={{ width: `${Math.min(llmOpsSummary.key_usage.usage_percent || 0, 100)}%` }}
                          />
                        </div>
                        <span className="llm-usage-text">
                          ${Number(llmOpsSummary.key_usage.usage || 0).toFixed(2)} used / ${llmOpsSummary.key_usage.limit || 0} daily
                        </span>
                      </div>
                      <div className="llm-ops-meta">
                        <span>Remaining: ${Number(llmOpsSummary.key_usage.limit_remaining || 0).toFixed(2)}</span>
                        <span>Reset: {llmOpsSummary.key_usage.limit_reset || 'daily'}</span>
                      </div>
                    </>
                  ) : (
                    <p className="api-description">No routed key usage available for the selected admin user.</p>
                  )}
                </div>

                <div className="admin-llm-ops-card">
                  <div className="api-card-header">
                    <h3>Mitigation Shortcuts</h3>
                  </div>
                  {llmOpsSummary.recommendations?.length ? (
                    <div className="llm-shortcut-list">
                      {llmOpsSummary.recommendations.map(rec => (
                        <div key={rec.id} className="llm-shortcut-item">
                          <div>
                            <strong>{rec.label}</strong>
                            <p>{rec.description}</p>
                          </div>
                          <div className="api-card-links">
                            {rec.id === 'raise_limit' && (
                              <a href="https://openrouter.ai/settings/keys" target="_blank" rel="noopener noreferrer" className="api-link api-link-primary">Raise Limit</a>
                            )}
                            {rec.id === 'lower_voice_tokens' && (
                              <a href="/ai-agent" className="api-link api-link-secondary">Review Voice Config</a>
                            )}
                            {rec.id === 'switch_model' && (
                              <a href="https://openrouter.ai/models" target="_blank" rel="noopener noreferrer" className="api-link api-link-secondary">Allowed Models</a>
                            )}
                            {rec.id === 'use_direct_google' && (
                              <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" className="api-link api-link-primary">Google AI Keys</a>
                            )}
                            {rec.id === 'headroom_low' && (
                              <a href="https://openrouter.ai/settings/keys" target="_blank" rel="noopener noreferrer" className="api-link api-link-warning">Add Credits</a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="api-description">No immediate mitigations recommended.</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="admin-empty-state">
                <p>{llmOpsLoading ? 'Loading incident data...' : 'No LLM incident summary available yet.'}</p>
                <p className="admin-empty-hint">Refresh this tab after the backend deploy if the cards stay empty.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (activeTab === 'esim') {
    return (
      <div className="admin-dashboard">
        <div className="admin-header">
          <div className="admin-header-content">
            <h1>Admin Dashboard</h1>
            <span className="admin-user-count">{totalUsers} users</span>
          </div>
          <div className="admin-tabs">
            {ADMIN_TABS.map(tab => (
              <button key={tab.id} className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>{tab.label}</button>
            ))}
          </div>
        </div>
        <div className="admin-esim-content">
          <EsimAdminDashboard user={user} />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-header-content">
          <h1>Admin Dashboard</h1>
          <span className="admin-user-count">{totalUsers} users</span>
        </div>
        <div className="admin-tabs">
          {ADMIN_TABS.map(tab => (
            <button key={tab.id} className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>{tab.label}</button>
          ))}
        </div>
      </div>

      <div className="admin-body">
        {/* Left panel: user list */}
        <div className="admin-sidebar">
          <div className="admin-search">
            <input
              type="text"
              placeholder="Search by email, name, or phone..."
              value={search}
              onChange={handleSearch}
              className="admin-search-input"
            />
          </div>

          {error && <div className="admin-error">{error}</div>}

          <div className="admin-user-list">
            {loading ? (
              <div className="admin-loading"><div className="loading-spinner" /></div>
            ) : filteredUsers.length === 0 ? (
              <div className="admin-empty">No users found</div>
            ) : (
              filteredUsers.map((u) => (
                <div
                  key={u.email || u._id}
                  className={`admin-user-item ${selectedUser === u.email ? 'selected' : ''}`}
                  onClick={() => selectUser(u)}
                >
                  <div className="admin-user-item-main">
                    <span className="admin-user-email">{u.email}</span>
                    <span className={`admin-user-badge ${isActive(u) ? 'active' : 'inactive'} ${u.disabled ? 'disabled' : ''}`}>
                      {u.disabled ? 'disabled' : isActive(u) ? 'active' : 'inactive'}
                    </span>
                  </div>
                  <div className="admin-user-item-sub">
                    {u.firstname || u.lastname
                      ? `${u.firstname || ''} ${u.lastname || ''}`.trim()
                      : 'No name'}
                    {' · '}
                    {formatDate(u.lastconnect)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right panel: user detail */}
        <div className="admin-detail">
          {!selectedUser ? (
            <div className="admin-detail-empty">
              <div className="admin-detail-empty-icon">👤</div>
              <p>Select a user from the list to view details</p>
            </div>
          ) : detailLoading ? (
            <div className="admin-loading"><div className="loading-spinner" /></div>
          ) : userDetail ? (
            <div className="admin-detail-content">
              {/* Account Info */}
              <section className="admin-section">
                <h3>Account Info</h3>
                <div className="admin-info-grid">
                  <div className="admin-info-item">
                    <label>Email</label>
                    <span>{userDetail.email}</span>
                  </div>
                  <div className="admin-info-item">
                    <label>Name</label>
                    <span>{[userDetail.firstname, userDetail.lastname].filter(Boolean).join(' ') || '—'}</span>
                  </div>
                  <div className="admin-info-item">
                    <label>Plan</label>
                    <span>{userDetail.freenumplan || userDetail.couponvalidated || 'None'}</span>
                  </div>
                  <div className="admin-info-item">
                    <label>Created</label>
                    <span>{formatDate(userDetail.created_at || userDetail._id)}</span>
                  </div>
                  <div className="admin-info-item">
                    <label>Last Login</label>
                    <span>{formatDate(userDetail.lastconnect)}</span>
                  </div>
                  <div className="admin-info-item">
                    <label>Status</label>
                    <span className={userDetail.disabled ? 'text-danger' : 'text-success'}>
                      {userDetail.disabled ? 'Disabled' : 'Active'}
                    </span>
                  </div>
                </div>
              </section>

              {/* eSIMs */}
              {userEsims.length > 0 && (
                <section className="admin-section">
                  <h3>eSIMs ({userEsims.length})</h3>
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Package</th>
                          <th>Status</th>
                          <th>ICCID</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userEsims.map((esim, i) => (
                          <tr key={esim._id || i}>
                            <td>{esim.label || esim.packageName}</td>
                            <td>
                              <span className={`admin-user-badge ${esim.status === 'ready' ? 'active' : esim.status === 'archived' ? 'disabled' : 'inactive'}`}>
                                {esim.status}
                              </span>
                            </td>
                            <td className="admin-mono-sm">{esim.iccid || '—'}</td>
                            <td>{formatDate(esim.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* Phone Numbers */}
              <section className="admin-section">
                <h3>Phone Numbers</h3>
                <div className="admin-info-grid">
                  <div className="admin-info-item">
                    <label>Free Numbers</label>
                    <span>
                      {userDetail.freenumbers && userDetail.freenumbers.length > 0
                        ? (Array.isArray(userDetail.freenumbers) ? userDetail.freenumbers.join(', ') : userDetail.freenumbers)
                        : 'None'}
                    </span>
                  </div>
                  <div className="admin-info-item">
                    <label>Target 1</label>
                    <span>{userDetail.targetnumber1 || '—'}</span>
                  </div>
                  <div className="admin-info-item">
                    <label>Target 2</label>
                    <span>{userDetail.targetnumber2 || '—'}</span>
                  </div>
                  <div className="admin-info-item">
                    <label>Verified Phone</label>
                    <span>{userDetail.verified_phone || '—'}</span>
                  </div>
                </div>
              </section>

              {/* Telephony Status */}
              <section className="admin-section">
                <h3>Telephony</h3>
                <div className="admin-info-grid">
                  <div className="admin-info-item">
                    <label>Sub-account SID</label>
                    <span className="admin-mono">{userDetail.subaccountAccountSID || '—'}</span>
                  </div>
                  <div className="admin-info-item">
                    <label>Voice App ID</label>
                    <span className="admin-mono">{userDetail.twilio_app_id || '—'}</span>
                  </div>
                  <div className="admin-info-item">
                    <label>Number Confirmed</label>
                    <span>{userDetail.freenumberconfirmed ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </section>

              {/* Actions */}
              <section className="admin-section">
                <h3>Actions</h3>
                {actionMessage && (
                  <div className="admin-action-message">{actionMessage}</div>
                )}
                <div className="admin-actions">
                  <button className="admin-btn admin-btn-warning" onClick={handleResetPassword}>
                    Reset Password
                  </button>
                  {lastTempPassword && (
                    <button className="admin-btn admin-btn-primary" onClick={handleSendLoginInfo}>
                      Send Login Info
                    </button>
                  )}
                  <button
                    className={`admin-btn ${userDetail.disabled ? 'admin-btn-success' : 'admin-btn-danger'}`}
                    onClick={handleToggleStatus}
                  >
                    {userDetail.disabled ? 'Enable Account' : 'Disable Account'}
                  </button>
                </div>
              </section>

              {/* Recent Calls */}
              <section className="admin-section">
                <h3>Recent Calls ({userCalls.length})</h3>
                {userCalls.length === 0 ? (
                  <p className="admin-empty-text">No calls found</p>
                ) : (
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>From</th>
                          <th>To</th>
                          <th>Duration</th>
                          <th>Status</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userCalls.slice(0, 20).map((call, i) => (
                          <tr key={call._id || i}>
                            <td className="admin-mono-sm">{call.From}</td>
                            <td className="admin-mono-sm">{call.To}</td>
                            <td>{call.Duration ? `${call.Duration}s` : '—'}</td>
                            <td>{call.Status || '—'}</td>
                            <td>{formatDateTime(call.StartTime || call.dateCreated)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Recent SMS */}
              <section className="admin-section">
                <h3>Recent SMS ({userSMS.length})</h3>
                {userSMS.length === 0 ? (
                  <p className="admin-empty-text">No messages found</p>
                ) : (
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>From</th>
                          <th>To</th>
                          <th>Body</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userSMS.slice(0, 20).map((msg, i) => (
                          <tr key={msg._id || i}>
                            <td className="admin-mono-sm">{msg.From}</td>
                            <td className="admin-mono-sm">{msg.To}</td>
                            <td className="admin-msg-body">{msg.Body}</td>
                            <td>{formatDateTime(msg.dateCreated)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          ) : (
            <div className="admin-detail-empty">
              <p>User not found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
